import { Switch as RACSwitch, type SwitchProps as RACSwitchProps } from "@ui/primitives/Switch";
import { composeRenderProps } from "@ui/primitives/shared";
import type { ReactNode } from "react";
import { switchControl } from "./styles";

export interface SwitchProps extends Omit<RACSwitchProps, "children"> {
  /** The text shown next to the control. */
  label: ReactNode;
  /** What flipping the switch does, under the label. */
  description?: ReactNode;
  /** `inline` sits the control before its label; `row` fills the width with the
   *  label leading and the control at the far end, for settings-style lists. */
  layout?: "inline" | "row";
}

export function Switch({ label, description, layout = "inline", className, ...props }: SwitchProps) {
  const slots = switchControl({ layout });

  const text = (
    <span className={slots.text()}>
      <span className={slots.label()}>{label}</span>
      {description && <span className={slots.description()}>{description}</span>}
    </span>
  );

  return (
    <RACSwitch {...props} className={composeRenderProps(className, (resolved) => slots.root({ className: resolved }))}>
      {({ isSelected, isHovered, isFocusVisible }) => {
        const control = (
          <span aria-hidden="true" className={slots.track({ isSelected, isHovered, isFocusVisible })}>
            <span className={slots.thumb({ isSelected })} />
          </span>
        );

        return layout === "row" ? (
          <>
            {text}
            {control}
          </>
        ) : (
          <>
            {control}
            {text}
          </>
        );
      }}
    </RACSwitch>
  );
}
